<script setup lang="ts">
import { useAdminZone } from "@/composables/useAdminZone";
import { topbarExcludedRoutes } from "@/configs/topbar_excluded_routes";
import { useAuthStore } from "@/stores/auth";
import { CustomerRole } from "@/types/customerRoles";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { type RouteRecordRaw, useRouter } from "vue-router";

const { t } = useI18n({
  useScope: "global", // Aggiungi questa opzione
});

const currentAssetId = ref<string | null>(null);
const router = useRouter();
const _authStore = useAuthStore();
const { currentContextRoutes, isInAdminZone } = useAdminZone();

const routes = ref<RouteRecordRaw[]>([]);

onMounted(async () => {
  const assetId = router.currentRoute.value.params.asset_id;
  currentAssetId.value = assetId ? String(assetId) : null;
  routes.value = await getRoutesForUser();
});

watch(
  () => router.currentRoute.value,
  async (newRoute) => {
    const assetId = newRoute.params.asset_id;
    currentAssetId.value = assetId ? String(assetId) : null;
    console.log(
      "[AppTopbar] Route changed, new asset_id:",
      currentAssetId.value,
    );
    routes.value = await getRoutesForUser();
  },
  { immediate: true }, // immediate: true might be redundant due to onMounted, but ensures it runs if onMounted is somehow delayed or skipped.
);

/**
 * Gets the list of routes that should be visible to the current user based on their role.
 *
 * This function filters the available routes based on several criteria:
 * - Routes must have a name
 * - Routes must have a minimum customer role defined
 * - Routes must not be explicitly hidden from the topbar
 * - User must have sufficient permissions to access the route
 *
 * The function compares the user's role level against the minimum required role
 * for each route using the CustomerRole enum ordering.
 *
 * @returns Promise<RouteRecordRaw[]> Array of route records visible to the current user
 */
async function getRoutesForUser(): Promise<RouteRecordRaw[]> {
  const userRole = await _authStore.getUserRole();
  const isAdminImpersonating =
    _authStore.isAdmin && _authStore.isImpersonatingCompany;
  const contextRoutes = currentContextRoutes.value;

  const routeChecks = await Promise.all(
    contextRoutes.map(async (route) => {
      const meta = route.meta || {}; // Ensure meta is an object for safe access

      if (route.name === undefined) {
        return { route, shouldInclude: false };
      }

      if (
        meta.hiddenInSelfPage === true &&
        route.name === router.currentRoute.value.name
      ) {
        return { route, shouldInclude: false };
      }

      if (meta.hiddenInTopBar === true) {
        return { route, shouldInclude: false };
      }

      // Check if route requires asset_id parameter but none is available
      const routeRequiresAssetId = route.path.includes(":asset_id");
      if (routeRequiresAssetId && !currentAssetId.value) {
        return { route, shouldInclude: false };
      }

      // For admin routes, check if user is admin
      if (meta.requiresAdmin === true) {
        if (!_authStore.isAdmin) {
          return { route, shouldInclude: false };
        }
        // Admin routes don't need role check, just admin status
        return { route, shouldInclude: true };
      }

      // For main app routes, check minimum customer role
      if (meta.minimumCustomerRole === undefined) {
        return { route, shouldInclude: false };
      }

      if (isAdminImpersonating) {
        return { route, shouldInclude: true };
      }

      const indexOfUserRole = Object.values(CustomerRole).indexOf(userRole);
      const indexOfMinimumCustomerRole = Object.values(CustomerRole).indexOf(
        meta.minimumCustomerRole as CustomerRole,
      );

      if (indexOfUserRole < indexOfMinimumCustomerRole) {
        return { route, shouldInclude: false };
      }

      return { route, shouldInclude: true };
    }),
  );

  return routeChecks
    .filter((check) => check.shouldInclude)
    .map((check) => check.route);
}

function isActiveRoute(routeName: string): boolean {
  return router.currentRoute.value.name === routeName;
}

function isHiddenInSelfPageNow(): boolean {
  return (
    router.currentRoute.value.meta.hiddenInSelfPage === true &&
    router.currentRoute.value.name?.toString() ===
      router.currentRoute.value.name
  );
}

/**
 * Generates route parameters for a given route, handling asset_id requirements properly.
 *
 * @param route The route record to generate parameters for
 * @returns Route location object with appropriate parameters
 */
function getRouteParams(route: RouteRecordRaw) {
  const routeRequiresAssetId = route.path.includes(":asset_id");

  if (routeRequiresAssetId && currentAssetId.value) {
    // Always navigate to master asset: drop any companion query
    return {
      name: route.name,
      params: { asset_id: currentAssetId.value },
      query: {},
    };
  }

  // Ensure we do not propagate companion query in any case
  return { name: route.name, query: {} };
}

/**
 * Checks if a RouterLink can be safely created for the given route.
 * This prevents the "Missing required param" error by ensuring routes
 * that require asset_id have one available.
 *
 * @param route The route record to check
 * @returns true if RouterLink can be created safely
 */
function canCreateRouterLink(route: RouteRecordRaw): boolean {
  const routeRequiresAssetId = route.path.includes(":asset_id");

  if (routeRequiresAssetId && !currentAssetId.value) {
    return false;
  }

  return true;
}
</script>

<template>
  <!-- Avoid showing topbar on excluded routes, included root route, and in admin zone -->
  <div
    v-if="
      _authStore.isLoggedIn &&
      router.currentRoute.value.path !== '/' &&
      !router.currentRoute.value.meta.hiddenInTopBar &&
      !isHiddenInSelfPageNow() &&
      !isInAdminZone
    "
    class="relative w-full topbar-wrapper"
  >
    <Tabs
      :modelValue="String(router.currentRoute.value.name)"
      class="w-full h-full"
      :value="String(router.currentRoute.value.name)"
    >
      <TabList class="flex h-full">
        <Tab
          v-for="tab in routes"
          :key="tab.name"
          :value="String(tab.name)"
          class="flex-1 h-full"
        >
          <RouterLink
            v-if="canCreateRouterLink(tab)"
            :to="getRouteParams(tab)"
            custom
            v-slot="{ href, navigate }"
          >
            <a
              :href="href"
              @click="navigate"
              class="flex flex-row topbar-item no-p w-full h-full text-center bg-surface-200 dark:bg-surface-800 transition-colors hover:bg-surface-200 dark:hover:bg-surface-700"
              :class="{
                'bg-primary-100 text-center text-primary-500 border-b-2 border-primary-500':
                  isActiveRoute(tab.name?.toString() ?? ''),
                'text-surface-900 dark:text-surface-0 border-b-2 border-transparent':
                  !isActiveRoute(tab.name?.toString() ?? ''),
              }"
            >
              <span
                class="text-2xl w-full h-full flex items-center justify-center font-medium"
                >{{ t(`topbar.route_names.${String(tab.name)}`) }}</span
              >
            </a>
          </RouterLink>
          <div
            v-else
            class="flex flex-row topbar-item no-p w-full h-full text-center bg-surface-100 dark:bg-surface-800 transition-colors hover:bg-surface-200 dark:hover:bg-surface-700 opacity-50 cursor-not-allowed border-b-2 border-transparent"
          >
            <span
              class="text-2xl w-full h-full flex items-center justify-center font-medium"
              >{{ t(`topbar.route_names.${String(tab.name)}`) }}</span
            >
          </div>
        </Tab>
      </TabList>
    </Tabs>
  </div>
</template>

<style scoped>
.robot-active {
  fill: green;
}
.robot-inactive {
  fill: black;
}
.no-p {
  padding: 0 !important;
}
.topbar-wrapper :deep(.p-tab) {
  margin: 0 !important;
  padding: 0 !important;
}
.topbar-wrapper :deep(.p-tablist-tab-list) {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100% !important;
  text-decoration: none;
  justify-content: space-center;
  color: inherit;
  transition: background-color 0.3s;
}
/* Hide default ink/active bars from the Tabs component to avoid double underlines */
.topbar-wrapper :deep([class*="ink-bar"]),
.topbar-wrapper :deep([class*="active-bar"]) {
  display: none !important;
}
/* Remove any default bottom borders on the tab list to ensure single underline */
.topbar-wrapper :deep(.p-tablist-tab-list),
.topbar-wrapper :deep(.p-tabs),
.topbar-wrapper :deep(.p-tablist) {
  border-bottom: 0 !important;
}
</style>
